import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Subscription } from 'rxjs';
import { AccountState } from '../../accounts/store/account/account.entity';
import { currentSelectedAccountDetail } from '../../accounts/store/account/account.selectors';
import { ToroLocationState } from '../../accounts/store/toro-location/toro-location.entity';
import { getCurrentLocation } from '../../accounts/store/toro-location/toro-location.selectors';
import { ApplicationStateManagerService } from '../../api/managers/application-state-manager.service';
import { MailCenterManagerService } from '../../api/managers/mail-center-manager.service';
import { MailCenterNotification } from '../../api/models/notifications/mail-center-notification.model';
import { ToroAccountDetailed } from '../../api/models/toro-account-detailed.model';
import { ToroLocation } from '../../api/models/toro-location.model';
import { UtilityService } from '../../services/utility.service';

@Component({
    selector: 'app-mail-center',
    templateUrl: './mail-center.component.html',
    styleUrls: ['./mail-center.component.less']
})
export class MailCenterComponent implements OnInit, OnDestroy
{
    private getRecentMessagesSubscription: Subscription;

    isLoading = false;
    messages: MailCenterNotification[] = [];
    data: { SubjectTitle: string, CreatedTimeUtc: Date, DisplayableMessage: string, IsRead: boolean }[];
    locationSubscription: Subscription;
    notificationsListChangeSubscription: Subscription;
    accountSubscription: Subscription;
    currentLocation: ToroLocation;
    currentAccount: ToroAccountDetailed

    // =========================================================================================================================================================
    // C'tor, Init and Destroy
    // =========================================================================================================================================================

    constructor(public applicationStateManager: ApplicationStateManagerService,
                public mailCenterManager: MailCenterManagerService,
                private accountStore: Store<AccountState>,
                private toroLocationStore: Store<ToroLocationState>,
                private utilityService:UtilityService,
        )
    {

    }

    ngOnInit()
    {

        this.accountSubscription = this.accountStore
        .select(currentSelectedAccountDetail)
        .subscribe((accountDetail) => {
            this.currentAccount = accountDetail;
        });

        this.locationSubscription =   this.toroLocationStore.select(getCurrentLocation)
        .subscribe(loc => {
            if (loc && loc !== null) {
                this.currentLocation = loc;
                if(this.currentLocation){
                    this.mailCenterManager.getRecentMessageForLocation(this.currentLocation.AccountId, this.currentLocation.Id);
                }
            }else{
                this.currentLocation = null;
                //this.messages = [];
                this.mailCenterManager.clearMessages();
            }
        });

        this.notificationsListChangeSubscription = this.mailCenterManager.notificationsListChange
            .pipe(untilDestroyed(this))
            .subscribe((notifications: MailCenterNotification[]) => {
                this.messages = this.utilityService.sortByDateDesc(notifications,'CreatedTimeUtc');
            });
    }

    ngOnDestroy()
    {
        if (this.getRecentMessagesSubscription)
        {
            this.getRecentMessagesSubscription.unsubscribe();
        }
        if(this.locationSubscription){
            this.locationSubscription.unsubscribe();
        }
        if(this.notificationsListChangeSubscription){
            this.notificationsListChangeSubscription.unsubscribe();
        }
        if (this.accountSubscription) {
            this.accountSubscription.unsubscribe();
        }
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    markMessageAsRead(messageId: string)
    {
        this.mailCenterManager.markMessagesAsRead([messageId]);
        this.mailCenterManager.markMessagesAsReadApiCall(this.currentAccount.Id, [messageId])
            .subscribe();
    }

    markAllMessagesAsRead()
    {
        const allMessageIds = this.createArrayOfAllMessageIds();
        this.mailCenterManager.markMessagesAsRead(allMessageIds);
        this.mailCenterManager.markMessagesAsReadApiCall(this.currentAccount.Id, allMessageIds)
            .subscribe();
    }

    deleteMessage(messageId: string)
    {
        this.mailCenterManager.deleteMessages([messageId]);
        this.mailCenterManager.deleteMessagesApiCall(this.currentAccount.Id, [messageId])
            .subscribe();
    }

    deleteAllMessages()
    {
        const allMessageIds = this.createArrayOfAllMessageIds();
        this.mailCenterManager.deleteMessages(allMessageIds);
        this.mailCenterManager.deleteMessagesApiCall(this.currentAccount.Id, allMessageIds)
            .subscribe();
    }

    refresh()
    {
        this.mailCenterManager.refresh(this.currentLocation.Id);
    }

    onHide()
    {
        this.applicationStateManager.mailCenterIsDisplayed = false;
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private createArrayOfAllMessageIds(): string[]
    {
        const allMessageIds = [];
        this.messages.forEach(message =>
        {
            allMessageIds.push(message.Id);
        });
        return allMessageIds;
    }

}
